import {
  Button,
  View,
  Input,
  Textarea,
  Checkbox,
  Label,
} from "@/components/ui";
import { cn } from "@/lib/utils";
import { InputFormControl } from "@/components/shared/input-form-control";
import { useId, useEffect } from "react";
import type { AddressFormData } from "@/store/auth/forms/useAddressForm";
import { useAddressForm } from "@/store/auth/forms/useAddressForm";
import { useAddressByPostalCodeStore } from "@/store/auth/addressByPostalCode";
import { useAuthState } from "@/store/auth";
import { LoginType } from "@/types/users";
import { FormControlError } from "@/components/shared/form-control-error";
import { Spin } from "@/components/shared/spin";
import { Controller } from "react-hook-form";
import { ErrorMessage } from "../error-message";
import { Typography } from "../../typography";
import { PosthogAnalytics } from "@/lib/monitoring/posthog";
import { useBookingState } from "@/store/booking/useBookingState";

export const AddAddressContent = ({
  isModal = true,
  loading,
  error,
  onAddAddress,
  isDefaultAddress,
}: {
  onAddAddress: (data: AddressFormData) => void;
  isModal?: boolean;
  loading: boolean;
  error?: string;
  isDefaultAddress?: boolean;
}) => {
  const id = useId();
  const { setType, setAddressFormReturn } = useAuthState();
  const formReturn = useAddressForm();
  const {
    control,
    handleSubmit,
    setValue,
    formState: { isSubmitted, errors },
  } = formReturn;

  const { loading: addAddressLoading, fetch: addressByPostalCode } =
    useAddressByPostalCodeStore();

  const { data: bookingState } = useBookingState();

  const onClickAddAddress = () => {
    PosthogAnalytics.track("user_sign_up_add_address");
    handleSubmit(onAddAddress)();
  };

  const onAddAddressByPostalCode = async (postalCode: string) => {
    const result = await addressByPostalCode({
      requestPayload: {
        postalCode,
      },
    });

    if (result.data?.address) {
      setValue("fullAddress", result.data.address);
    }
  };

  useEffect(() => {
    setType(LoginType.SignUp);
    setAddressFormReturn(formReturn);
  }, []);

  useEffect(() => {
    if (bookingState.selectedAddress) {
      setValue("label", bookingState.selectedAddress.label || "");
      setValue("postalCode", bookingState.selectedAddress.postalCode || "");
      setValue("fullAddress", bookingState.selectedAddress.fullAddress || "");
    }
  }, [bookingState.selectedAddress]);

  useEffect(() => {
    setValue("primary", !!isDefaultAddress);
  }, [isDefaultAddress]);

  return (
    <View className={cn("flex flex-col", isModal ? "gap-2" : "gap-4")}>
      <View className="pb-3">
        <Typography variant="label-md">Add Address</Typography>
      </View>
      <InputFormControl
        name="postalCode"
        label="Postal Code"
        control={control}
        component={Input}
        componentProps={{
          placeholder: "Enter Postal Code",
          suffix: addAddressLoading && <Spin />,
        }}
        changeFallBack={(value) => {
          if (!value || value.length < 6 || value.length > 10) return;
          onAddAddressByPostalCode(value);
        }}
      />
      <InputFormControl
        name="fullAddress"
        label="Address"
        className="pt-3"
        control={control}
        component={Textarea}
        componentProps={{
          placeholder: "Enter Address",
        }}
      />
      <InputFormControl
        name="label"
        label="Label as"
        className="pt-3"
        control={control}
        component={Input}
        componentProps={{
          placeholder: "Eg. Home, Office",
          maxLength: 30,
        }}
      />
      <Label className="block pb-1 text-sm" htmlFor={id}>
        Unit Number
      </Label>

      <View className="flex flex-row gap-3">
        <InputFormControl
          name="unitFloor"
          className="flex-1 pb-0"
          control={control}
          component={Input}
          componentProps={{
            placeholder: "Floor",
            id,
          }}
          hideErrorMessage
        />
        <InputFormControl
          name="apartmentNumber"
          className="flex-1 pb-0"
          control={control}
          component={Input}
          componentProps={{
            placeholder: "Apt no",
          }}
          hideErrorMessage
        />
      </View>

      {errors?.unitFloor || errors.apartmentNumber ? (
        <FormControlError error="Floor and Apt No. required. Please indicate 0 if not applicable." />
      ) : (
        <Typography variant="body-sm" color="secondary-text" className="mt-1">
          If not applicable, enter 0 for Floor or Apt No.
        </Typography>
      )}

      <Controller
        control={control}
        name="primary"
        render={({ field: { onChange, value } }) => (
          <View className="flex flex-row items-center gap-2 pt-3">
            <Checkbox
              id="is_primary_checkbox"
              checked={value}
              disabled={isDefaultAddress}
              onCheckedChange={onChange}
            />
            <Label htmlFor="is_primary_checkbox" className="cursor-pointer">
              Set as default address
            </Label>
          </View>
        )}
      />

      {isSubmitted && (
        <ErrorMessage errorMessage={error || ""} className="mt-3" />
      )}
      <Button
        variant="primary"
        color="CTA"
        rounded="full"
        onClick={onClickAddAddress}
        className="mt-3"
        loading={loading}
        children="Add Address"
      />
    </View>
  );
};
